/**
 * Bean Snake — the game itself.
 *
 * Boundary: this component takes a seed and hands back an input log. It
 * knows nothing about tries, scores, networks, or the leaderboard — the
 * server recomputes the score by replaying the log through the same
 * `@shared/arcade/snake` engine, so this component has no authority over
 * anything. No API calls, no localStorage, no score submission here.
 *
 * `GameState` lives in a ref, not React state: one re-render per tick would
 * fight the render loop. Ticks are driven by a fixed-timestep `setInterval`
 * (not `requestAnimationFrame` — jsdom does not fake rAF reliably, and the
 * test suite drives time with `vi.advanceTimersByTime`).
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createInitialState, step } from '@shared/arcade/snake';
import { GRID, MAX_TICKS, type Direction, type GameState, type InputEvent } from '@shared/arcade/types';

interface SnakeCanvasProps {
  seed: number;
  onGameOver: (result: { score: number; inputs: InputEvent[] }) => void;
}

// Lets a caller pull the run-in-progress out of a still-live instance — the
// case that matters is a player closing out mid-run: `onGameOver` only ever
// fires from inside the tick loop on death, so without this the entire log
// (which lives in a ref, per the file header) would vanish with the unmount
// and a paid-for ranked try would be lost with zero trace. Safe to call at
// any moment: `replay` on the server just keeps stepping an unsteered snake
// from the last recorded input until it dies on its own, so a partial log
// still scores honestly.
export interface SnakeCanvasHandle {
  getRunState(): { score: number; inputs: InputEvent[] };
}

// Local mirror of the engine's reversal table. Not exported from
// @shared/arcade/snake — the component needs its own copy to decide whether
// a press is worth recording at all, before the engine ever sees it.
const OPPOSITE: Record<Direction, Direction> = {
  up: 'down', down: 'up', left: 'right', right: 'left',
};

// Internal render resolution. Purely a rendering detail — the canvas is
// displayed at CSS size (see the JSX below, `width: 100%` up to a max-width)
// with `image-rendering: pixelated` doing the upscale, so this does not
// touch `GRID`, tick timing, or anything the server replays.
const CELL = 20;
const CANVAS_SIZE = GRID * CELL;
const SWIPE_THRESHOLD = 16; // px — below this, a touch is a tap, not a swipe

// 3-2-1-GO before the tick loop starts. See the effect below for why this
// never touches the engine.
const COUNTDOWN_SECONDS = 3;
const COUNTDOWN_STEP_MS = 1000;
const GO_DISPLAY_MS = 500;
export const COUNTDOWN_MS = COUNTDOWN_SECONDS * COUNTDOWN_STEP_MS;

function tickDuration(beansEaten: number): number {
  return Math.max(60, 140 - 4 * beansEaten);
}

function drawBoard(ctx: CanvasRenderingContext2D, state: GameState) {
  // Deep teal, not the typical Snake black — pairs with the app's
  // coral/cream/teal-ink palette, and leaves the brown/gold beans
  // (a coffee-brown board would swallow them) visible against it.
  ctx.fillStyle = '#0b3b3a';
  ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

  // Subtle cell-boundary grid for texture — low alpha on purpose, this
  // should read as texture, not as a chessboard.
  ctx.strokeStyle = 'rgba(255,255,255,0.04)';
  ctx.lineWidth = 1;
  for (let i = 1; i < GRID; i++) {
    const p = i * CELL + 0.5;
    ctx.beginPath();
    ctx.moveTo(p, 0);
    ctx.lineTo(p, CANVAS_SIZE);
    ctx.moveTo(0, p);
    ctx.lineTo(CANVAS_SIZE, p);
    ctx.stroke();
  }

  const beanColor = state.bean.roast === 'green' ? '#c98b4b'
    : state.bean.roast === 'medium' ? '#8a5a2b'
    : '#3b2314';
  ctx.fillStyle = beanColor;
  ctx.fillRect(state.bean.x * CELL, state.bean.y * CELL, CELL, CELL);
  if (state.bean.roast === 'dark') {
    ctx.strokeStyle = '#ffcc00';
    ctx.lineWidth = 2;
    ctx.strokeRect(state.bean.x * CELL + 1, state.bean.y * CELL + 1, CELL - 2, CELL - 2);
  }

  state.snake.forEach((seg, i) => {
    ctx.fillStyle = i === 0 ? '#5be06c' : '#3dba4c';
    ctx.fillRect(seg.x * CELL, seg.y * CELL, CELL, CELL);
  });

  ctx.font = '10px monospace';
  ctx.textBaseline = 'top';
  ctx.fillStyle = '#ffcc00';
  ctx.textAlign = 'left';
  ctx.fillText(String(state.score), 4, 4);

  if (state.bean.expiresAtTick !== null) {
    const remaining = Math.max(0, state.bean.expiresAtTick - state.tick);
    ctx.fillStyle = '#f96a55';
    ctx.textAlign = 'right';
    ctx.fillText(String(remaining), CANVAS_SIZE - 4, 4);
  }
}

const SnakeCanvas = forwardRef<SnakeCanvasHandle, SnakeCanvasProps>(function SnakeCanvas(
  { seed, onGameOver },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Engine state — a ref, deliberately. See file header.
  //
  // `useRef`'s initializer runs once, at mount, so a `seed` prop change on an
  // already-mounted instance does NOT start a new game — all the per-run
  // refs below (state, inputs, pending direction, beans-eaten, game-over
  // latch) stay put. A caller that wants a fresh run for a new seed must
  // remount the component (e.g. `<SnakeCanvas key={seed} .../>`) rather than
  // rely on the prop alone. This mirrors the ordinary React "uncontrolled
  // initial value" pattern and keeps the reset path to a single, well-worn
  // mechanism instead of a second bespoke one inside this component.
  const stateRef = useRef<GameState>(createInitialState(seed));
  const inputsRef = useRef<InputEvent[]>([]);
  // The direction queued to apply on the NEXT tick, or null if no turn is
  // pending. Doubles as "what a fresh press should be compared against"
  // so two presses between ticks collapse into one recorded event instead
  // of two entries at the same tick (which the server's strictly-increasing
  // tick check would reject).
  const pendingDirRef = useRef<Direction | null>(null);
  const beansEatenRef = useRef(0);
  const gameOverFiredRef = useRef(false);
  const durationRef = useRef(tickDuration(0));
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  // Gates input during the pre-run countdown. A ref (not derived from the
  // `countdown` display state below) so `recordDirection` — called from
  // synchronous DOM event handlers — always reads the current value without
  // closing over a stale one. Presses while this is true are dropped, not
  // queued: a queued press would eventually record against a tick that
  // hadn't happened yet when the player pressed it.
  const countingDownRef = useRef(true);
  // Display-only: what the countdown overlay shows. `null` once it's done
  // and the loop is live. Starts at COUNTDOWN_SECONDS so the first paint
  // already shows "3" instead of a blank frame before the first timer fires.
  const [countdown, setCountdown] = useState<number | 'GO' | null>(COUNTDOWN_SECONDS);

  // Keep the latest callback without re-running the tick effect on every
  // parent render — the effect below intentionally has an empty dep array.
  const onGameOverRef = useRef(onGameOver);
  onGameOverRef.current = onGameOver;

  // Reads the same refs `onGameOver` reads at natural death — this is
  // deliberately just a snapshot getter, not a second source of truth. If
  // the player closes out during the countdown, `stateRef` is still the
  // untouched initial state and `inputsRef` is still empty: a legitimate
  // (if unplayed) run that the server's `replay` scores honestly as 0,
  // rather than something this needs to special-case.
  useImperativeHandle(ref, () => ({
    getRunState: () => ({ score: stateRef.current.score, inputs: [...inputsRef.current] }),
  }), []);

  const recordDirection = (press: Direction) => {
    if (countingDownRef.current) return;
    const state = stateRef.current;
    if (state.dead) return;
    const effective = pendingDirRef.current ?? state.dir;
    // Redundant or a 180° reversal of what will actually be current by the
    // time this applies — ignored, same as the engine would ignore it, so
    // the log never carries a no-op.
    if (press === effective || press === OPPOSITE[effective]) return;

    if (pendingDirRef.current !== null) {
      // A turn is already queued for the same not-yet-applied tick —
      // overwrite it rather than append, so the log never gets two entries
      // for one tick.
      const last = inputsRef.current[inputsRef.current.length - 1];
      if (last) last[1] = press;
    } else {
      inputsRef.current.push([state.tick + 1, press]);
    }
    pendingDirRef.current = press;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d') ?? null;
    // jsdom returns null for getContext('2d') without the optional `canvas`
    // package. Rendering degrades to a no-op rather than crashing the loop —
    // the tests only assert on the input log and DOM structure, never pixels.
    const draw = (state: GameState) => { if (ctx) drawBoard(ctx, state); };

    const clearGameLoop = () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const scheduleInterval = (duration: number) => {
      clearGameLoop();
      durationRef.current = duration;
      intervalRef.current = setInterval(runTick, duration);
    };

    function runTick() {
      const prev = stateRef.current;
      if (prev.dead) return;

      const dir = pendingDirRef.current;
      pendingDirRef.current = null;

      const next = step(prev, dir);
      stateRef.current = next;
      if (next.score > prev.score) beansEatenRef.current += 1;
      draw(next);

      // MAX_TICKS is the server replay's own ceiling (`replay` stops calling
      // `step` once `state.tick >= MAX_TICKS`), so a run that reaches it here
      // would keep ticking locally while the server-authoritative score is
      // already frozen. Ending the run as an ordinary game-over — rather than
      // leaving the loop running until the player quits or the tab closes —
      // is what gets the score submitted at all instead of lost. See C2 in
      // the whole-branch review: without this ceiling, perimeter-hugging play
      // reaches ticks the validator used to reject wholesale.
      if (next.dead || next.tick >= MAX_TICKS) {
        clearGameLoop();
        if (!gameOverFiredRef.current) {
          gameOverFiredRef.current = true;
          onGameOverRef.current({ score: next.score, inputs: inputsRef.current });
        }
        return;
      }

      const nextDuration = tickDuration(beansEatenRef.current);
      if (nextDuration !== durationRef.current) scheduleInterval(nextDuration);
    }

    draw(stateRef.current);

    // 3-2-1-GO before `scheduleInterval` (and therefore `runTick`) is ever
    // called. This is deliberately just a countdown of setInterval steps —
    // it never calls `step`, never touches `stateRef`/`inputsRef`, and never
    // advances a tick. `countingDownRef` flips to false in the same tick
    // that the loop is scheduled, which is also the earliest moment
    // `recordDirection` will accept a press — so a press landing exactly on
    // "GO" is recorded against the real next tick, never a countdown tick
    // that doesn't exist in the engine's numbering.
    let secondsLeft = COUNTDOWN_SECONDS;
    let goTimeout: ReturnType<typeof setTimeout> | null = null;
    const countdownInterval = setInterval(() => {
      secondsLeft -= 1;
      if (secondsLeft > 0) {
        setCountdown(secondsLeft);
        return;
      }
      clearInterval(countdownInterval);
      setCountdown('GO');
      countingDownRef.current = false;
      scheduleInterval(durationRef.current);
      // Purely cosmetic — lets "GO" linger on screen a beat after the loop
      // (and input) is already live, then clears the overlay.
      goTimeout = setTimeout(() => setCountdown(null), GO_DISPLAY_MS);
    }, COUNTDOWN_STEP_MS);

    return () => {
      clearInterval(countdownInterval);
      if (goTimeout !== null) clearTimeout(goTimeout);
      clearGameLoop();
    };
    // Empty on purpose: nothing in this effect reads `seed` (it was already
    // consumed by stateRef's initializer above) or `onGameOver` (read via
    // onGameOverRef instead, updated every render) — including either here
    // would re-run the whole loop setup for no reason, or, worse, on every
    // parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const t = e.touches[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    const t = e.changedTouches[0];
    if (!start || !t) return;

    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_THRESHOLD) return; // tap, not a swipe

    const dir: Direction = Math.abs(dx) > Math.abs(dy)
      ? (dx > 0 ? 'right' : 'left')
      : (dy > 0 ? 'down' : 'up');
    recordDirection(dir);
  };

  // Cream button, coral glyph, true black stepped edge — `pixel-corners-sm`
  // is a `clip-path`, so a plain CSS `border` gets clipped away at the
  // corners. The accent instead comes from nesting: an outer bg-primary
  // (black) shell with a few px of padding, holding an inner bg-background
  // (cream) face — the padding IS the visible edge.
  const dPadButton = (label: Direction, glyph: string, extraClass: string) => (
    <button
      type="button"
      aria-label={label}
      onClick={() => recordDirection(label)}
      className={`pixel-corners-sm bg-primary p-[3px] w-16 h-16 active:translate-y-[1px] ${extraClass}`}
    >
      <span className="pixel-corners-sm bg-background text-coral font-heading text-[22px] w-full h-full flex items-center justify-center">
        {glyph}
      </span>
    </button>
  );

  return (
    <div className="flex flex-col items-center justify-between w-full h-full gap-6">
      <div className="relative w-full max-w-[440px]">
        <canvas
          ref={canvasRef}
          width={CANVAS_SIZE}
          height={CANVAS_SIZE}
          className="w-full aspect-square pixel-corners bg-teal-ink touch-none [image-rendering:pixelated]"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        />
        {countdown !== null && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span
              className="font-heading text-coral leading-none"
              style={{ fontSize: countdown === 'GO' ? '44px' : '64px' }}
            >
              {countdown}
            </span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 grid-rows-3 gap-2 w-[208px]">
        <div />
        {dPadButton('up', '▲', 'col-start-2 row-start-1')}
        <div />
        {dPadButton('left', '◀', 'col-start-1 row-start-2')}
        <div className="col-start-2 row-start-2" />
        {dPadButton('right', '▶', 'col-start-3 row-start-2')}
        <div />
        {dPadButton('down', '▼', 'col-start-2 row-start-3')}
        <div />
      </div>
    </div>
  );
});

export default SnakeCanvas;
